import type { ExerciseDef } from './types'

// ─── 8.1 Tokenization ─────────────────────────────────────────────────────────

export const vocab: ExerciseDef = {
  starter: `// A character-level tokenizer.
// 1. buildVocab(text): every distinct character, sorted, numbered from 0.
//    itos ("int to string") is the list; stoi ("string to int") maps back.
function buildVocab(text: string): { itos: string[]; stoi: Record<string, number> } {
  const itos: string[] = []
  const stoi: Record<string, number> = {}
  return { itos, stoi }
}

// 2. encode: text → list of ids.   3. decode: ids → text.
function encode(text: string, stoi: Record<string, number>): number[] {
  return []
}
function decode(ids: number[], itos: string[]): string {
  return ""
}

const v = buildVocab("hello world")
console.log(v.itos)
console.log(encode("hello", v.stoi), decode(encode("hello", v.stoi), v.itos))
`,
  solution: `function buildVocab(text: string): { itos: string[]; stoi: Record<string, number> } {
  const itos = [...new Set(text.split(""))].sort()
  const stoi: Record<string, number> = {}
  itos.forEach((ch, i) => (stoi[ch] = i))
  return { itos, stoi }
}

function encode(text: string, stoi: Record<string, number>): number[] {
  return text.split("").map(ch => stoi[ch])
}
function decode(ids: number[], itos: string[]): string {
  return ids.map(i => itos[i]).join("")
}

const v = buildVocab("hello world")
console.log(v.itos)
console.log(encode("hello", v.stoi), decode(encode("hello", v.stoi), v.itos))
`,
  tests: `
test("the vocabulary of 'hello world' has 8 characters, sorted", () => expect(buildVocab("hello world").itos, "itos").toEqual([" ", "d", "e", "h", "l", "o", "r", "w"]))
test("stoi is the reverse of itos", () => expect(buildVocab("abcab").stoi, "stoi").toEqual({ a: 0, b: 1, c: 2 }))
test("encode gives one id per character", () => {
  const v = buildVocab("hello world")
  expect(encode("hello", v.stoi), "encode('hello')").toEqual([3, 2, 4, 4, 5])
})
test("decode(encode(x)) gives x back", () => {
  const v = buildVocab(data.stories)
  const s = "the little fox"
  expect(decode(encode(s, v.stoi), v.itos), "round trip").toBe(s)
})
`,
}

// ─── 8.2 Bigram model ─────────────────────────────────────────────────────────

export const bigram: ExerciseDef = {
  starter: `const chars = ".abcdefghijklmnopqrstuvwxyz"   // "." marks the start and end of a name
const idx = (ch: string) => chars.indexOf(ch)

// 1. countBigrams: N[a][b] = how often letter b follows letter a, over all names.
//    Wrap each name with dots: "emma" → ". e m m a ."
function countBigrams(names: string[]): number[][] {
  const N = mat.zeros(27, 27)
  return N
}

// 2. toProbabilities: add 1 to every count (smoothing), then divide each row by its sum.
function toProbabilities(N: number[][]): number[][] {
  return N
}

// 3. averageNLL: average of −ln P[a][b] over every bigram in the names. (This is the loss!)
function averageNLL(names: string[], P: number[][]): number {
  return 0
}

const N = countBigrams(data.names)
const P = toProbabilities(N)
console.log("loss:", averageNLL(data.names, P), "   (random guessing: ln 27 =", Math.log(27), ")")

// Generate names: start at ".", repeatedly sample the next letter from that row.
for (let k = 0; k < 8; k++) {
  let ix = 0, name = ""
  while (true) {
    ix = random.sample(P[ix])
    if (ix === 0 || name.length > 15) break
    name += chars[ix]
  }
  console.log(name)
}
`,
  solution: `const chars = ".abcdefghijklmnopqrstuvwxyz"
const idx = (ch: string) => chars.indexOf(ch)

function countBigrams(names: string[]): number[][] {
  const N = mat.zeros(27, 27)
  for (const name of names) {
    const cs = [".", ...name.split(""), "."]
    for (let i = 0; i < cs.length - 1; i++) N[idx(cs[i])][idx(cs[i + 1])]++
  }
  return N
}

function toProbabilities(N: number[][]): number[][] {
  return N.map(row => {
    const total = vec.sum(row) + row.length
    return row.map(c => (c + 1) / total)
  })
}

function averageNLL(names: string[], P: number[][]): number {
  let total = 0, count = 0
  for (const name of names) {
    const cs = [".", ...name.split(""), "."]
    for (let i = 0; i < cs.length - 1; i++) {
      total -= Math.log(P[idx(cs[i])][idx(cs[i + 1])])
      count++
    }
  }
  return total / count
}

const N = countBigrams(data.names)
const P = toProbabilities(N)
console.log("loss:", averageNLL(data.names, P), "   (random guessing: ln 27 =", Math.log(27), ")")

for (let k = 0; k < 8; k++) {
  let ix = 0, name = ""
  while (true) {
    ix = random.sample(P[ix])
    if (ix === 0 || name.length > 15) break
    name += chars[ix]
  }
  console.log(name)
}
`,
  tests: `
test("counts one bigram per transition", () => {
  const __N = countBigrams(["ab", "a"])
  expect([__N[0][1], __N[1][2], __N[2][0], __N[1][0]], "[.→a, a→b, b→., a→.]").toEqual([2, 1, 1, 1])
})
test("every probability row sums to 1", () => {
  const __P = toProbabilities(countBigrams(data.names))
  expect(__P.map(r => vec.sum(r)), "row sums").toBeCloseTo(vec.fill(27, 1), 1e-9)
})
test("smoothing: nothing has probability 0", () => expect(Math.min(...toProbabilities(countBigrams(["ab"])).flat()) > 0, "smallest probability > 0").toBeTruthy())
test("the loss on names is well below random guessing (ln 27 ≈ 3.30)", () => {
  const __P = toProbabilities(countBigrams(data.names))
  expect(averageNLL(data.names, __P), "averageNLL").toBeBetween(2.2, 2.7)
})
`,
}

// ─── 8.3 Embeddings ───────────────────────────────────────────────────────────

export const analogy: ExerciseDef = {
  starter: `// Toy 4-D word vectors: [royal, male, adult, animal]
const E: Record<string, number[]> = {
  king:   [1, 1, 1, 0],
  queen:  [1, -1, 1, 0],
  prince: [1, 1, -1, 0],
  princess: [1, -1, -1, 0],
  man:    [0, 1, 1, 0],
  woman:  [0, -1, 1, 0],
  boy:    [0, 1, -1, 0],
  girl:   [0, -1, -1, 0],
  dog:    [0, 0.2, 1, 1],
  puppy:  [0, 0.2, -1, 1],
}

// 1. nearest(v, exclude): the word whose vector has the highest cosine similarity to v,
//    skipping any word in exclude. (vec.cosine is available.)
function nearest(v: number[], exclude: string[] = []): string {
  return ""
}

// 2. analogy(a, b, c): "a is to b as ? is to c" → nearest(E[a] − E[b] + E[c]),
//    excluding a, b and c themselves.
function analogy(a: string, b: string, c: string): string {
  return ""
}

console.log("king − man + woman =", analogy("king", "man", "woman"))
console.log("puppy − dog + man =", analogy("puppy", "dog", "man"))
`,
  solution: `const E: Record<string, number[]> = {
  king:   [1, 1, 1, 0],
  queen:  [1, -1, 1, 0],
  prince: [1, 1, -1, 0],
  princess: [1, -1, -1, 0],
  man:    [0, 1, 1, 0],
  woman:  [0, -1, 1, 0],
  boy:    [0, 1, -1, 0],
  girl:   [0, -1, -1, 0],
  dog:    [0, 0.2, 1, 1],
  puppy:  [0, 0.2, -1, 1],
}

function nearest(v: number[], exclude: string[] = []): string {
  let best = ""
  let bestSim = -Infinity
  for (const w of Object.keys(E)) {
    if (exclude.includes(w)) continue
    const s = vec.cosine(v, E[w])
    if (s > bestSim) {
      bestSim = s
      best = w
    }
  }
  return best
}

function analogy(a: string, b: string, c: string): string {
  const v = vec.add(vec.sub(E[a], E[b]), E[c])
  return nearest(v, [a, b, c])
}

console.log("king − man + woman =", analogy("king", "man", "woman"))
console.log("puppy − dog + man =", analogy("puppy", "dog", "man"))
`,
  tests: `
test("the nearest word to king's own vector is king", () => expect(nearest(E.king), "nearest(E.king)").toBe("king"))
test("exclusion works", () => expect(nearest(E.king, ["king"]) !== "king", "nearest(E.king, ['king']) isn't king").toBeTruthy())
test("king − man + woman = queen", () => expect(analogy("king", "man", "woman"), "analogy").toBe("queen"))
test("prince − boy + girl = princess", () => expect(analogy("prince", "boy", "girl"), "analogy").toBe("princess"))
test("puppy − dog + man = boy", () => expect(analogy("puppy", "dog", "man"), "analogy").toBe("boy"))
`,
}

// ─── 8.4 Neural language model ────────────────────────────────────────────────

export const nlm: ExerciseDef = {
  starter: `// A neural language model (Bengio et al., 2003), trained on names.
// It reads the previous 3 characters and predicts the next one.
const chars = ".abcdefghijklmnopqrstuvwxyz"
const block = 3

// Build the dataset: every (3 previous characters → next character) pair.
const X: number[][] = []
const Y: number[] = []
for (const name of data.names) {
  let ctx = [0, 0, 0]
  for (const ch of [...name.split(""), "."]) {
    const ix = chars.indexOf(ch)
    X.push(ctx)
    Y.push(ix)
    ctx = [...ctx.slice(1), ix]
  }
}
console.log(X.length, "training examples, e.g.", X[3].map(i => chars[i]).join(""), "→", chars[Y[3]])

// The model: embed each of the 3 characters (10 numbers each), concatenate
// to 30 numbers, a hidden layer of 64 tanh neurons, then 27 output scores.
const emb = new Embedding(27, 10)
const hidden = new Linear(block * 10, 64)
const output = new Linear(64, 27)
const params = [...emb.parameters(), ...hidden.parameters(), ...output.parameters()]
const opt = new Adam(params, { lr: 0.01 })

// forward(contexts): logits, one row per context.
//   - for each position t, embed contexts.map(c => c[t])   → (B × 10)
//   - Tensor.concatCols of the three                         → (B × 30)
//   - hidden.forward(...).tanh(), then output.forward(...)   → (B × 27)
function forward(contexts: number[][]): Tensor {
  return Tensor.zeros(contexts.length, 27)
}

const chart = plot.live("loss")
for (let step = 0; step < 2000; step++) {
  // 1. pick 32 random examples (random.int(0, X.length))
  // 2. loss = forward(batch contexts).crossEntropy(batch targets)
  // 3. opt.zeroGrad(); loss.backward(); opt.step()
  // 4. every 20 steps: chart.push(loss.item())
}

function evaluate(): number {
  return forward(X).crossEntropy(Y).item()
}
console.log("final loss on all names:", evaluate(), "  (bigram model: about 2.45)")

function sample(): string {
  let ctx = [0, 0, 0], out = ""
  for (let i = 0; i < 16; i++) {
    const probs = forward([ctx]).softmax().row(0)
    const ix = random.sample(probs)
    if (ix === 0) break
    out += chars[ix]
    ctx = [...ctx.slice(1), ix]
  }
  return out
}
for (let k = 0; k < 8; k++) console.log(sample())
`,
  solution: `const chars = ".abcdefghijklmnopqrstuvwxyz"
const block = 3

const X: number[][] = []
const Y: number[] = []
for (const name of data.names) {
  let ctx = [0, 0, 0]
  for (const ch of [...name.split(""), "."]) {
    const ix = chars.indexOf(ch)
    X.push(ctx)
    Y.push(ix)
    ctx = [...ctx.slice(1), ix]
  }
}
console.log(X.length, "training examples, e.g.", X[3].map(i => chars[i]).join(""), "→", chars[Y[3]])

const emb = new Embedding(27, 10)
const hidden = new Linear(block * 10, 64)
const output = new Linear(64, 27)
const params = [...emb.parameters(), ...hidden.parameters(), ...output.parameters()]
const opt = new Adam(params, { lr: 0.01 })

function forward(contexts: number[][]): Tensor {
  const parts = range(block).map(t => emb.forward(contexts.map(c => c[t])))
  const x = Tensor.concatCols(parts)
  const h = hidden.forward(x).tanh()
  return output.forward(h)
}

const chart = plot.live("loss")
for (let step = 0; step < 2000; step++) {
  const idx = range(32).map(() => random.int(0, X.length))
  const loss = forward(idx.map(i => X[i])).crossEntropy(idx.map(i => Y[i]))
  opt.zeroGrad()
  loss.backward()
  opt.step()
  if (step % 20 === 0) chart.push(loss.item())
}

function evaluate(): number {
  return forward(X).crossEntropy(Y).item()
}
console.log("final loss on all names:", evaluate(), "  (bigram model: about 2.45)")

function sample(): string {
  let ctx = [0, 0, 0], out = ""
  for (let i = 0; i < 16; i++) {
    const probs = forward([ctx]).softmax().row(0)
    const ix = random.sample(probs)
    if (ix === 0) break
    out += chars[ix]
    ctx = [...ctx.slice(1), ix]
  }
  return out
}
for (let k = 0; k < 8; k++) console.log(sample())
`,
  tests: `
test("forward returns one row of 27 scores per context", () => expect(forward([[0, 0, 0], [1, 2, 3]]).shape, "shape").toEqual([2, 27]))
test("the trained model beats the bigram model (loss < 2.4)", () => expect(evaluate(), "loss on all names").toBeLessThan(2.4))
`,
}

// ─── 8.5 Sampling ─────────────────────────────────────────────────────────────

export const sampling: ExerciseDef = {
  starter: `// 1. withTemperature(logits, T): divide every score by T, then softmax.
function withTemperature(logits: number[], T: number): number[] {
  return vec.softmax(logits)
}

// 2. topK(probs, k): keep the k largest probabilities, set the rest to 0,
//    and rescale so they sum to 1 again.
function topK(probs: number[], k: number): number[] {
  return probs
}

// 3. sampleIndex(probs): pick an index with the given probabilities.
//    Draw r = random.random(); walk through probs subtracting each one;
//    the index where r drops below 0 is your pick.
function sampleIndex(probs: number[]): number {
  return 0
}

const logits = [2, 1, 0.5, -1]
console.log(withTemperature(logits, 0.5), topK(vec.softmax(logits), 2))
console.log(range(20).map(() => sampleIndex(topK(vec.softmax(logits), 2))))
`,
  solution: `function withTemperature(logits: number[], T: number): number[] {
  return vec.softmax(logits.map(l => l / T))
}

function topK(probs: number[], k: number): number[] {
  const threshold = [...probs].sort((a, b) => b - a)[k - 1]
  let kept = 0
  const out = probs.map(p => {
    if (p >= threshold && kept < k) {
      kept++
      return p
    }
    return 0
  })
  const total = vec.sum(out)
  return out.map(p => p / total)
}

function sampleIndex(probs: number[]): number {
  let r = random.random()
  for (let i = 0; i < probs.length; i++) {
    r -= probs[i]
    if (r < 0) return i
  }
  return probs.length - 1
}

const logits = [2, 1, 0.5, -1]
console.log(withTemperature(logits, 0.5), topK(vec.softmax(logits), 2))
console.log(range(20).map(() => sampleIndex(topK(vec.softmax(logits), 2))))
`,
  tests: `
test("temperature 1 is plain softmax", () => expect(withTemperature([1, 2, 3], 1), "withTemperature([1,2,3], 1)").toBeCloseTo(vec.softmax([1, 2, 3])))
test("low temperature sharpens", () => expect(withTemperature([1, 2], 0.1)[1], "top prob at T = 0.1").toBeGreaterThan(0.99))
test("high temperature flattens", () => expect(withTemperature([1, 2], 100)[1], "top prob at T = 100").toBeLessThan(0.51))
test("topK keeps the k biggest and renormalises", () => expect(topK([0.1, 0.5, 0.15, 0.25], 2), "topK(..., 2)").toBeCloseTo([0, 0.5 / 0.75, 0, 0.25 / 0.75]))
test("sampleIndex follows the probabilities", () => {
  random.seed(5)
  const counts = [0, 0, 0]
  for (let i = 0; i < 20000; i++) counts[sampleIndex([0.2, 0.5, 0.3])]++
  expect(counts.map(c => c / 20000), "observed frequencies").toBeCloseTo([0.2, 0.5, 0.3], 0.02)
})
test("sampleIndex never picks a zero-probability item", () => {
  random.seed(9)
  const picks = range(2000).map(() => sampleIndex([0, 1, 0]))
  expect(picks.every(p => p === 1), "all picks are index 1").toBeTruthy()
})
`,
}
